import { useEffect, useRef, useState } from 'react';

export interface PreloadedBlobState {
  blob: Blob | null;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

/** Resolves protected binary data before the user presses a gesture-gated
 * action such as navigator.share(). The loader is deliberately excluded from
 * effect identity; only the asset key/source and an explicit retry restart it. */
export function usePreloadedBlob(options: {
  assetKey: string;
  enabled: boolean;
  initial?: Blob | null;
  load: () => Promise<Blob>;
}): PreloadedBlobState {
  const { assetKey, enabled, initial = null, load } = options;
  const loadRef = useRef(load);
  loadRef.current = load;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<PreloadedBlobState, 'retry'>>({ blob: null, loading: false, error: null });

  useEffect(() => {
    let active = true;
    if (!assetKey || !enabled) {
      setState({ blob: null, loading: false, error: null });
      return () => { active = false; };
    }
    if (initial) {
      setState({ blob: initial, loading: false, error: null });
      return () => { active = false; };
    }
    setState({ blob: null, loading: true, error: null });
    void loadRef.current().then((blob) => {
      if (active) setState({ blob, loading: false, error: null });
    }).catch((error: unknown) => {
      if (active) setState({ blob: null, loading: false, error });
    });
    return () => { active = false; };
  }, [assetKey, attempt, enabled, initial]);

  return { ...state, retry: () => setAttempt((value) => value + 1) };
}
